import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Notification, NotificationHubRow } from 'app/layout/common/notifications/notifications.types';
import { apiSilentRequestOptions } from 'app/shared/interceptors/api-message-context';

const INBOX = '/api/notification-hub/inbox';

/** In-app channel of the Notification Hub. */
const CHANNEL = 'message';

/** Fallback bell icon per business category (hub `type`). */
const TYPE_ICONS: Record<string, string> = {
    billing: 'heroicons_outline:cash',
    payment: 'heroicons_outline:credit-card',
    subscription: 'heroicons_outline:credit-card',
    support: 'heroicons_outline:ticket',
    mail: 'heroicons_outline:mail',
    store: 'heroicons_outline:shopping-bag',
    system: 'heroicons_outline:bell',
};

/**
 * Notification Hub inbox for the signed-in user of the current app.
 *
 * Scope comes from the request interceptor: `X-APP-CODE` (per app) and the
 * bearer token (per user) — the hub never returns another user's rows.
 */
@Injectable({
    providedIn: 'root'
})
export class NotificationsService
{
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private _unreadCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient)
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    /**
     * Getter for the server-side unread count (badge)
     */
    get unreadCount$(): Observable<number>
    {
        return this._unreadCount.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load the inbox for the current app + user
     */
    getAll(limit: number = 50): Observable<Notification[]>
    {
        return this._httpClient.post<any>(`${INBOX}/list`, {
            channel: CHANNEL,
            limit
        }, apiSilentRequestOptions()).pipe(
            map((response) => {
                const payload = response?.payload ?? response ?? {};
                const rows: NotificationHubRow[] = payload.data ?? [];
                const notifications = rows.map((row) => this._fromHub(row));

                this._notifications.next(notifications);
                this._unreadCount.next(Number(payload.unread_count ?? this._countUnread(notifications)));

                return notifications;
            })
        );
    }

    /**
     * Store notifications on the service
     *
     * @param notifications
     */
    store(notifications: Notification[]): Observable<Notification[]>
    {
        // Load the notifications
        this._notifications.next(notifications ?? []);
        this._unreadCount.next(this._countUnread(notifications ?? []));

        // Return the notifications
        return this.notifications$;
    }

    /**
     * Toggle the read status of a notification
     *
     * @param notification
     */
    toggleRead(notification: Notification): Observable<Notification>
    {
        const path = notification.read ? 'unread' : 'read';

        return this._httpClient.post<any>(`${INBOX}/${path}`, {
            id: notification.id
        }, apiSilentRequestOptions()).pipe(
            map((response) => {
                const row: NotificationHubRow | null = response?.payload?.notification ?? null;

                return row ? this._fromHub(row) : { ...notification, read: !notification.read };
            }),
            switchMap((updated) => this._replace(updated))
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean>
    {
        return this._httpClient.post<any>(`${INBOX}/read-all`, {
            channel: CHANNEL
        }, apiSilentRequestOptions()).pipe(
            switchMap(() => this.notifications$.pipe(take(1))),
            map((notifications) => {
                this._notifications.next(notifications.map((notification) => ({ ...notification, read: true })));
                this._unreadCount.next(0);

                return true;
            })
        );
    }

    /**
     * Remove the notification from the inbox (archived on the hub)
     *
     * @param id
     */
    delete(id: string): Observable<boolean>
    {
        return this._httpClient.post<any>(`${INBOX}/archive`, { id }, apiSilentRequestOptions()).pipe(
            switchMap(() => this.notifications$.pipe(take(1))),
            map((notifications) => {
                const remaining = notifications.filter((notification) => notification.id !== id);

                this._notifications.next(remaining);
                this._unreadCount.next(this._countUnread(remaining));

                return true;
            })
        );
    }

    /**
     * Refresh the unread badge without reloading the list
     */
    refreshUnreadCount(): Observable<number>
    {
        return this._httpClient.post<any>(`${INBOX}/unread-count`, {
            channel: CHANNEL
        }, apiSilentRequestOptions()).pipe(
            map((response) => Number(response?.payload?.unread_count ?? 0)),
            tap((count) => this._unreadCount.next(count))
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _replace(updated: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            map((notifications) => {
                const next = notifications.map((notification) => notification.id === updated.id ? updated : notification);

                this._notifications.next(next);
                this._unreadCount.next(this._countUnread(next));

                return updated;
            })
        );
    }

    private _countUnread(notifications: Notification[]): number
    {
        return notifications.filter((notification) => !notification.read).length;
    }

    private _fromHub(row: NotificationHubRow): Notification
    {
        const data = row.data ?? {};
        const link = `${data.link ?? data.deep_link ?? ''}`.trim();

        return {
            id: row.id,
            title: row.title,
            description: row.body ?? undefined,
            time: row.created_at ?? new Date().toISOString(),
            read: row.status !== 'unread',
            icon: `${data.icon ?? ''}`.trim() || TYPE_ICONS[row.type] || TYPE_ICONS.system,
            image: `${data.image ?? ''}`.trim() || undefined,
            link: link || undefined,
            useRouter: link ? (data.use_router ?? link.startsWith('/')) : undefined,
            channel: row.channel,
            type: row.type,
        };
    }
}
